import { type JSX, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { AddOnVisibilityOperationType, SubscriptionAddonCode } from '../../common/enums.js';
import { fetchAddOnsVisibilities } from '../../common/fetch.js';
import { useSearchParams } from '../../common/hooks/useSearchParams';
import { noAdditionalServicesMsg, t } from '../../common/i18n/index.js';
import { CATEGORY_URL } from '../../common/utils/categoryUtils.js';
import {
  getSubscriptionUrlId,
  isMobileIdAddOn,
  isReachabilityWithMobile,
  mobiilivarmenneNotInUseAddOn,
} from '../../common/utils/subscriptionUtils.js';
import { SourceSystem } from '../../generated/api/models.js';
import { Loading } from '../Loading/index.js';
import { AddOnExternalLink } from '../SubscriptionAdditionalService/AddOnExternalLink.js';
import { AddOnManagementButtons } from '../SubscriptionAdditionalService/AddOnManagementButtons.js';
import { MobileIdAdditionalService } from '../SubscriptionAdditionalService/MobileIdAdditionalService.js';
import { shouldNotShowMobileId } from '../SubscriptionAdditionalService/subscriptionAddOnUtils.js';
import { AddOnRow } from './AddOnRow.js';
import {
  MOBIILITURVA_ADDON,
  calculateBaseDependency,
  checkCurrentCombination,
  determineSelectedLanguage,
  getAddOnsByCodes,
  getDefaultSelectedLanguage,
  isAddonVisible,
  isMobileVoiceAddOn,
  isMobileVoiceLanguageUpdated,
  reCalculateBaseDependency,
  updateVisibilitiesIfRing,
} from './addOnDependencyUtils.js';
import { isLiikkuvaWifiSubscription } from './subscriptionDetailsUtils.js';
import type { BaseRules } from './addOnDependencyUtils.js';
import type { CategoryKey } from '../../common/utils/categoryUtils.js';
import type { AddOnVisibility, Subscription } from '../../generated/api/models.js';
import type {
  AssociationRecord,
  DependencyRecord,
} from '../../legacy/cloudsense-add-on-dependency-engine/AddOnManagerOnline';

export interface AdditionalServicesContentProps {
  category: CategoryKey;
  subscription: Subscription;
  changeOfferAllowed: boolean;
  isMobileIdEnabledForCompany: boolean;
  isMobileIdEnabledForEoeUser: boolean;
  baseRules: BaseRules;
  isEmployee: boolean;
  isAssetWithMobiiliturva: boolean;
  addOnVisibilities: AddOnVisibility[];
  showMobileReachabilityAddon?: boolean;
  isEditing: boolean;
  setEditing: (editing: boolean) => void;
  hideEditButton?: boolean;
}

interface AdditionalServicesAccordionContentProps {
  category: CategoryKey;
  subscription: Subscription;
  changeOfferAllowed: boolean;
  isMobileIdEnabledForCompany?: boolean;
  isMobileIdEnabledForEoeUser?: boolean;
  addOnAssociations?: AssociationRecord[];
  addOnDependencies?: DependencyRecord[];
  pbxSolutions?: Subscription[];
  showMobileReachabilityAddon?: boolean;
}

const buildAddOnDetailsPath = (addOnCode: string, category: CategoryKey, subscription: Subscription) =>
  `${paths.PS_HOME}/${CATEGORY_URL[category]}/${getSubscriptionUrlId(subscription)}/${addOnCode}`;

// Use function to get elements (instead of wrapper component) to improve null/undefined check clarity
const getMobileIdRows = (
  subscription: Subscription,
  isEmployee: boolean,
  showEditView: boolean,
  category: CategoryKey,
  isMobileIdEnabledForCompany = false,
  isMobileIdEnabledForEoeUser = false
): JSX.Element[] => {
  if (shouldNotShowMobileId(isEmployee, isMobileIdEnabledForCompany, isMobileIdEnabledForEoeUser)) {
    return [];
  }
  const mobileIdAddOns = subscription.details?.selectedAddOns?.filter(selectedAddOn =>
    isMobileIdAddOn(selectedAddOn.addOnGroup)
  );
  // Always show Mobiilivarmenne for employees. If there is no active one, show not in use alternative.
  if (mobileIdAddOns?.length === 0 && isEmployee) {
    mobileIdAddOns.push(mobiilivarmenneNotInUseAddOn);
  }

  return (
    mobileIdAddOns?.map(mobileIdAddOn => (
      <MobileIdAdditionalService
        key={`mobileid-addon-${mobileIdAddOn.addOnCode}`}
        selectedAddOn={mobileIdAddOn}
        additionalServicePath={buildAddOnDetailsPath(mobileIdAddOn.addOnCode, category, subscription)}
        showEditView={showEditView}
        isEmployee={isEmployee}
      />
    )) || []
  );
};

export const AdditionalServicesContent = ({
  category,
  subscription,
  changeOfferAllowed,
  addOnVisibilities,
  baseRules,
  isEmployee,
  isAssetWithMobiiliturva,
  isMobileIdEnabledForCompany,
  isMobileIdEnabledForEoeUser,
  showMobileReachabilityAddon = false,
  isEditing,
  setEditing,
  hideEditButton = false,
}: AdditionalServicesContentProps) => {
  const subscriptionAddOns = subscription.details?.selectedAddOns;
  const subscriptionAddOnCodes = subscriptionAddOns?.map(addOn => addOn.addOnCode) || [];
  const { search } = useLocation();
  const [addOnCodesToAdd, setAddOnCodesToAdd] = useState<string[]>([]);
  const [addOnCodesToRemove, setAddOnCodesToRemove] = useState<string[]>([]);
  const [linkedAddOnCodesToRemove, setLinkedAddOnCodesToRemove] = useState<string[]>([]);
  const [isValidCombination, setIsValidCombination] = useState(false);
  const [editabilityRecords, setEditabilityRecords] = useState(
    calculateBaseDependency(baseRules, subscriptionAddOnCodes, addOnVisibilities, isAssetWithMobiiliturva, isEmployee)
  );
  const [selectedLanguage, setSelectedLanguage] = useState(determineSelectedLanguage(subscriptionAddOns));

  const resetEditView = () => {
    setAddOnCodesToAdd([]);
    setAddOnCodesToRemove([]);
    setEditing(false);
    setEditabilityRecords(
      calculateBaseDependency(baseRules, subscriptionAddOnCodes, addOnVisibilities, isAssetWithMobiiliturva, isEmployee)
    );
    setLinkedAddOnCodesToRemove([]);
    setIsValidCombination(false);
    setSelectedLanguage(determineSelectedLanguage(subscriptionAddOns));
  };

  const handleAddOnSelectionChange = (checked: boolean, addOnVisibility: AddOnVisibility) => {
    // Assets doesn't have addons included like the normal SF based subs
    const isAddOnIncludedInSubscription =
      subscriptionAddOnCodes.includes(addOnVisibility.addOnGuidCode) || isAssetWithMobiiliturva;
    const addedItemsWhenChecked = isAddOnIncludedInSubscription
      ? addOnCodesToAdd
      : [...addOnCodesToAdd, addOnVisibility.addOnGuidCode];
    const addedItemsWhenUnchecked = isAddOnIncludedInSubscription
      ? addOnCodesToAdd
      : addOnCodesToAdd.filter(item => item !== addOnVisibility.addOnGuidCode);
    const addedItems = checked ? addedItemsWhenChecked : addedItemsWhenUnchecked;

    const removedItemsWhenChecked = isAddOnIncludedInSubscription
      ? addOnCodesToRemove.filter(item => item !== addOnVisibility.addOnGuidCode)
      : addOnCodesToRemove;
    const removedItemsWhenUnchecked = isAddOnIncludedInSubscription
      ? [...addOnCodesToRemove, addOnVisibility.addOnGuidCode]
      : addOnCodesToRemove;
    const removedItems = checked ? removedItemsWhenChecked : removedItemsWhenUnchecked;

    const editability = reCalculateBaseDependency(
      baseRules,
      addedItems,
      removedItems,
      addOnVisibility.addOnGuidCode,
      subscriptionAddOnCodes,
      addOnVisibilities,
      isAssetWithMobiiliturva,
      isEmployee
    );
    const currentCombination = checkCurrentCombination(
      baseRules,
      addedItems,
      removedItems,
      subscriptionAddOnCodes,
      addOnVisibilities
    );
    const linkedItemsToRemove = currentCombination.openingFeeCodesToDelete;

    if (isMobileVoiceAddOn(addOnVisibility.addOnGuidCode)) {
      const languageIfChecked = isAddOnIncludedInSubscription
        ? determineSelectedLanguage(subscriptionAddOns)
        : getDefaultSelectedLanguage();
      setSelectedLanguage(checked ? languageIfChecked : undefined);
    }

    setAddOnCodesToAdd(addedItems);
    setAddOnCodesToRemove(removedItems);
    setLinkedAddOnCodesToRemove(linkedItemsToRemove);
    setEditabilityRecords(editability);
    setIsValidCombination(currentCombination.isValid);
  };

  const addOnsByRole: AddOnVisibility[] = addOnVisibilities
    .filter(addOnVisibility =>
      isAddonVisible(isAssetWithMobiiliturva, subscriptionAddOnCodes, addOnVisibility, isEmployee)
    )
    .filter(addOnVisibility => !isMobileIdAddOn(addOnVisibility.addOn?.addOnGroup))
    .filter(
      addOnVisibility => showMobileReachabilityAddon || !isReachabilityWithMobile(addOnVisibility.addOn?.addOnCode)
    );

  const addOnRows = addOnsByRole?.map((addOnVisibility, i) => (
    <AddOnRow
      key={`addl-service-addon-${i}`}
      addOnVisibility={addOnVisibility}
      showEditView={isEditing}
      editabilityRecords={editabilityRecords}
      subscriptionAddOnCodes={subscriptionAddOnCodes}
      addOnCodesToAdd={addOnCodesToAdd}
      addOnCodesToRemove={addOnCodesToRemove}
      isAssetWithMobiiliturva={isAssetWithMobiiliturva}
      isEmployee={isEmployee}
      handleAddOnSelectionChange={handleAddOnSelectionChange}
      baseRules={baseRules}
      additionalServicePath={buildAddOnDetailsPath(addOnVisibility.addOnGuidCode, category, subscription) + search}
      selectedLanguage={selectedLanguage}
      onChangeLanguage={(language?: string) => {
        setSelectedLanguage(language);
        const currentCombination = checkCurrentCombination(
          baseRules,
          addOnCodesToAdd,
          addOnCodesToRemove,
          subscriptionAddOnCodes,
          addOnVisibilities
        );
        setIsValidCombination(currentCombination.isValid);
      }}
    />
  ));

  const mobileIdRows = getMobileIdRows(
    subscription,
    isEmployee,
    isEditing,
    category,
    isMobileIdEnabledForCompany,
    isMobileIdEnabledForEoeUser
  );

  const isMobileVoiceUpdated = isMobileVoiceLanguageUpdated(
    [...addOnCodesToAdd, ...addOnCodesToRemove],
    subscriptionAddOns,
    selectedLanguage
  );
  const updatedAddOns = isMobileVoiceUpdated
    ? getAddOnsByCodes([SubscriptionAddonCode.VOICE_MAIL], addOnVisibilities, subscriptionAddOns)
    : [];
  const shouldDisableConfirm = () => {
    if (addOnCodesToAdd.length === 0 && addOnCodesToRemove.length === 0 && updatedAddOns.length === 0) {
      return true;
    }
    return !isValidCombination;
  };
  const removalAddOnCodesToDisplay = [...addOnCodesToRemove, ...linkedAddOnCodesToRemove];
  const managementButtons = (
    <AddOnManagementButtons
      showEditView={isEditing}
      resetEditView={() => resetEditView()}
      setShowEditView={show => setEditing(show)}
      shouldDisableConfirm={shouldDisableConfirm()}
      addedAddOns={getAddOnsByCodes(addOnCodesToAdd, addOnVisibilities, subscriptionAddOns)}
      removedAddOns={getAddOnsByCodes(removalAddOnCodesToDisplay, addOnVisibilities, subscriptionAddOns)}
      updatedAddOns={updatedAddOns}
      selectedLanguage={selectedLanguage}
      subscription={subscription}
      category={category}
      changeOfferAllowed={changeOfferAllowed}
      isEmployee={isEmployee}
    />
  );

  return addOnRows?.length > 0 || mobileIdRows.length > 0 ? (
    <>
      {!isEditing && !hideEditButton && managementButtons}
      {addOnRows}
      {mobileIdRows}
      {!hideEditButton && <AddOnExternalLink linkHref={paths.INSTRUCTIONS_SUBSCRIPTION_ADDONS} />}
      {isEditing && managementButtons}
    </>
  ) : (
    <div className={dsClass.MARGIN_TOP_4}>{t.EIYK(noAdditionalServicesMsg)}</div>
  );
};

export const AdditionalServicesAccordionContent = ({
  category,
  subscription,
  changeOfferAllowed,
  isMobileIdEnabledForCompany = false,
  addOnAssociations,
  addOnDependencies,
  pbxSolutions,
  isMobileIdEnabledForEoeUser = false,
  showMobileReachabilityAddon,
}: AdditionalServicesAccordionContentProps) => {
  const [addOnVisibilities, setAddOnVisibilities] = useState<AddOnVisibility[] | undefined>(undefined);
  const [loadingAddOnVisibility, setLoadingAddOnVisibility] = useState(true);
  const isAssetWithMobiiliturva = Boolean(subscription.details?.asset?.hasMobiiliturva);
  const { mdmId } = useSearchParams<{ mdmId?: string }>();
  const [isEditing, setEditing] = useState(false);

  useEffect(() => {
    if (!addOnVisibilities) {
      // Assets (Tellus based subscriptions) may have Mobiiliturva addon but no commercial product, which could be used to fetch the addon data,
      // so put a hard coded version to visibility object list.
      if (subscription.sourceSystem === SourceSystem.TELLUS && !subscription.migrated) {
        setAddOnVisibilities([MOBIILITURVA_ADDON]);
        setLoadingAddOnVisibility(false);
      } else if (isLiikkuvaWifiSubscription(subscription.subscriptionName)) {
        setAddOnVisibilities([]);
        setLoadingAddOnVisibility(false);
      } else {
        setLoadingAddOnVisibility(true);
        fetchAddOnsVisibilities(
          subscription.commercialProductCode || '',
          AddOnVisibilityOperationType.CHANGE,
          mdmId
        ).then(res => {
          setAddOnVisibilities(res.addOnVisibilities);
          setLoadingAddOnVisibility(false);
        });
      }
    }
  }, [
    addOnVisibilities,
    subscription.commercialProductCode,
    subscription.details?.selectedAddOns,
    isAssetWithMobiiliturva,
    subscription.sourceSystem,
    subscription.migrated,
    subscription.subscriptionName,
    mdmId,
  ]);

  const isAllDataLoaded = Boolean(!loadingAddOnVisibility && addOnAssociations && addOnDependencies);
  const baseRules: BaseRules = {
    commercialProductId: subscription.commercialProductId ?? '',
    commercialProductCode: subscription.commercialProductCode ?? '',
    addOnAssociations: addOnAssociations ?? [],
    addOnDependencies: addOnDependencies ?? [],
  };
  const updatedVisibilities = updateVisibilitiesIfRing(subscription, pbxSolutions, addOnVisibilities) || [];

  return isAllDataLoaded ? (
    <AdditionalServicesContent
      category={category}
      changeOfferAllowed={changeOfferAllowed}
      subscription={subscription}
      isMobileIdEnabledForCompany={isMobileIdEnabledForCompany}
      isMobileIdEnabledForEoeUser={isMobileIdEnabledForEoeUser}
      addOnVisibilities={updatedVisibilities}
      baseRules={baseRules}
      isEmployee={true}
      isAssetWithMobiiliturva={isAssetWithMobiiliturva}
      showMobileReachabilityAddon={showMobileReachabilityAddon}
      isEditing={isEditing}
      setEditing={setEditing}
    />
  ) : (
    <Loading />
  );
};
